/**
 * Universal team role definitions.
 * Shared config for RoleSection component and any role-related logic.
 */

export interface UserLite {
  _id: string
  firstName?: string
  lastName?: string
  name?: string
  nickName?: string
  email?: string
  avatar?: string
}

/** All possible role array keys on the Team model */
export type TeamRoleKey =
  | 'admins'
  | 'coaches'
  | 'players'
  | 'goalkeepers'
  | 'externalOrganizers'
  | 'treasurers'
  | 'captains'
  | 'timekeepers'

/** Singular form used for invite/remove actions */
export type TeamRoleSingular =
  | 'admin'
  | 'coach'
  | 'player'
  | 'goalkeeper'
  | 'externalOrganizer'
  | 'treasurer'
  | 'captain'
  | 'timekeeper'

export interface TeamRoleConfig {
  /** Plural key matching the DB field (e.g. 'admins') */
  key: TeamRoleKey
  /** Singular key (e.g. 'admin') */
  singular: TeamRoleSingular
  /** i18n key for section title (plural) */
  titleKey: string
  /** i18n key for singular role label */
  labelKey: string
  /** i18n key for "Add …" button */
  addLabelKey: string
  /** i18n key for empty state text */
  emptyLabelKey: string
  /** Badge color used in member lists */
  badgeColor: string
  /** Minimum count (e.g. admins must keep ≥ 1) */
  minCount?: number
}

/**
 * Ordered map of all team roles.
 * Order here determines the default rendering order.
 */
export const TEAM_ROLES: Record<TeamRoleKey, TeamRoleConfig> = {
  admins: {
    key: 'admins',
    singular: 'admin',
    titleKey: 'admins',
    labelKey: 'admin',
    addLabelKey: 'addAdmin',
    emptyLabelKey: 'noAdmins',
    badgeColor: '#ff9800',
    minCount: 1,
  },
  coaches: {
    key: 'coaches',
    singular: 'coach',
    titleKey: 'coaches',
    labelKey: 'coach',
    addLabelKey: 'addCoach',
    emptyLabelKey: 'noCoaches',
    badgeColor: '#2196f3',
  },
  players: {
    key: 'players',
    singular: 'player',
    titleKey: 'players',
    labelKey: 'player',
    addLabelKey: 'addPlayer',
    emptyLabelKey: 'noPlayers',
    badgeColor: '#607d8b',
  },
  goalkeepers: {
    key: 'goalkeepers',
    singular: 'goalkeeper',
    titleKey: 'goalies',
    labelKey: 'goalkeeper',
    addLabelKey: 'addGoalkeeper',
    emptyLabelKey: 'noGoalkeepers',
    badgeColor: '#9c27b0',
  },
  externalOrganizers: {
    key: 'externalOrganizers',
    singular: 'externalOrganizer',
    titleKey: 'externalOrganizers',
    labelKey: 'externalOrganizer',
    addLabelKey: 'addExternalOrganizer',
    emptyLabelKey: 'noExternalOrganizers',
    badgeColor: '#795548',
  },
  treasurers: {
    key: 'treasurers',
    singular: 'treasurer',
    titleKey: 'treasurers',
    labelKey: 'treasurer',
    addLabelKey: 'addTreasurer',
    emptyLabelKey: 'noTreasurers',
    badgeColor: '#4caf50',
  },
  captains: {
    key: 'captains',
    singular: 'captain',
    titleKey: 'captains',
    labelKey: 'captain',
    addLabelKey: 'addCaptain',
    emptyLabelKey: 'noCaptains',
    badgeColor: '#e91e63',
  },
  timekeepers: {
    key: 'timekeepers',
    singular: 'timekeeper',
    titleKey: 'timekeepers',
    labelKey: 'timekeeper',
    addLabelKey: 'addTimekeeper',
    emptyLabelKey: 'noTimekeepers',
    badgeColor: '#00bcd4',
  },
}

/** Default order for rendering role sections */
export const TEAM_ROLES_ORDER: TeamRoleKey[] = [
  'admins',
  'coaches',
  'players',
  'goalkeepers',
  'externalOrganizers',
  'treasurers',
  'captains',
  'timekeepers',
]

/** Map singular → plural key */
export const SINGULAR_TO_PLURAL: Record<TeamRoleSingular, TeamRoleKey> = {
  admin: 'admins',
  coach: 'coaches',
  player: 'players',
  goalkeeper: 'goalkeepers',
  externalOrganizer: 'externalOrganizers',
  treasurer: 'treasurers',
  captain: 'captains',
  timekeeper: 'timekeepers',
}

/** Get user display name */
export function getUserLabel(user: UserLite): string {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`
  if (user.name) return user.name
  if (user.nickName) return user.nickName
  return user.email || 'Neznámý'
}

/** Map a raw user object (from API) to UserLite */
export function mapUserLite(u: any): UserLite {
  if (typeof u === 'string') return { _id: u }
  return {
    _id: u._id,
    firstName: u.firstName,
    lastName: u.lastName,
    name: u.name,
    nickName: u.nickName,
    email: u.email,
    avatar: u.avatar,
  }
}
